import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button from '../../../components/ui/Button';
import useProgressHeartbeat from './useProgressHeartbeat';

const Wrap = styled.div`
  background: #000;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
  aspect-ratio: 16 / 9;
  max-width: 960px;
  max-height: 540px;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
  }
`;

const FooterNote = styled.div`
  background: #FEF3C7;
  color: #92400E;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 12px;
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export function toEmbedUrl(url) {
  if (!url) return url;
  // YouTube watch → embed
  const ytWatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytWatch) return `https://www.youtube.com/embed/${ytWatch[1]}`;
  // Vimeo regular → player
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo && !url.includes('player.vimeo.com')) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
}

/**
 * YouTube / Vimeo embed player. We can't hook into the player's real
 * "ended" event without pulling in their SDKs, so we expose a manual
 * "Mark as watched" button that the learner taps once they finish the
 * video. The heartbeat still fires every 30s to keep progress live.
 */
export default function EmbedPlayer({
  contentItem,
  assignmentId,
  onCompleted,
  onProgress,
}) {
  const { t } = useTranslation();
  const completedRef = useRef(contentItem.isCompleted ?? false);

  const getState = () => ({
    contentItemId: contentItem.id,
    clientCompleted: completedRef.current,
  });

  const flush = useProgressHeartbeat(assignmentId, getState, (res) => {
    if (res?.isCompleted) onCompleted?.();
    onProgress?.(res);
  });

  useEffect(() => () => { flush(); }, [flush]);

  const markDone = async () => {
    completedRef.current = true;
    await flush({ clientCompleted: true });
    onCompleted?.();
  };

  const embedUrl = toEmbedUrl(contentItem.url);

  return (
    <div>
      <Wrap>
        <iframe
          src={embedUrl}
          title={contentItem.originalFileName || 'Embedded video'}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </Wrap>
      {assignmentId && !contentItem.isCompleted && (
        <FooterNote>
          <span>{t('training.embedCompleteHint')}</span>
          <Button type="button" $variant="primary" onClick={markDone}>
            {t('training.markAsWatched')}
          </Button>
        </FooterNote>
      )}
    </div>
  );
}
